import { useState } from 'react';
import { Link } from 'react-router-dom';
import { products } from '@/mocks/products';

const DEAL_IDS = [3, 5, 7, 9, 1, 19, 30, 15];

const COMMISSION_LABELS: Record<number, string> = {
  3: '💰 High Commission',
  5: '🔥 Top Earner',
  7: '⭐ Fan Favorite',
  9: '💰 High Commission',
  1: '🔥 Top Earner',
  19: '⭐ Fan Favorite',
  30: '💰 High Commission',
  15: '🔥 Top Earner',
};

const DEAL_TAGS: Record<number, string> = {
  3: 'BEST VALUE',
  5: 'EDITOR\'S PICK',
  7: 'MOST LOVED',
  9: 'VIRAL PICK',
  1: 'BESTSELLER',
  19: 'VET APPROVED',
  30: 'MUST HAVE',
  15: 'CAT CALM',
};

export default function BestDealsStrip() {
  const [saved, setSaved] = useState<Record<string, boolean>>({});

  const dealProducts = DEAL_IDS.map((id) =>
    products.find((p) => p.id === String(id))
  ).filter(Boolean) as typeof products;

  const handleSave = (id: string, e: React.MouseEvent) => {
    e.preventDefault();
    setSaved((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  return (
    <section className="py-14 bg-gradient-to-b from-amber-50 to-orange-50 border-t border-orange-100">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <div className="flex items-center gap-2 px-3 py-1.5 bg-orange-500 rounded-full">
              <span className="w-2 h-2 bg-white rounded-full animate-pulse inline-block"></span>
              <span className="text-xs font-bold text-white tracking-wide uppercase">Live Deals</span>
            </div>
            <div>
              <h2 className="text-2xl font-black text-gray-900 leading-tight">
                Best Deals Today 🛍️
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Hand-picked by our editors · Updated daily · Affiliate links support MeowCentral
              </p>
            </div>
          </div>
          <Link
            to="/shop"
            className="hidden md:flex items-center gap-2 px-5 py-2.5 bg-white border border-orange-200 rounded-full text-sm font-semibold text-orange-600 hover:bg-orange-500 hover:text-white hover:border-orange-500 transition-all duration-200 whitespace-nowrap cursor-pointer shadow-sm"
          >
            View All Deals
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>

        {/* Deal Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-4 gap-4">
          {dealProducts.map((product) => {
            const discount = product.originalPrice
              ? Math.round(((product.originalPrice - product.price) / product.originalPrice) * 100)
              : null;
            const tag = DEAL_TAGS[Number(product.id)];
            const commLabel = COMMISSION_LABELS[Number(product.id)];

            return (
              <a
                key={product.id}
                href={product.link}
                target="_blank"
                rel="nofollow noopener noreferrer"
                className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-orange-100 hover:border-orange-300 flex flex-col cursor-pointer"
              >
                {/* Image */}
                <div className="relative w-full h-44 overflow-hidden bg-gray-50">
                  <img
                    src={product.image}
                    alt={product.name}
                    className="w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500"
                  />
                  {/* Tag badge */}
                  <div className="absolute top-2 left-2 px-2 py-0.5 bg-orange-500 text-white text-[10px] font-black rounded-full tracking-wide">
                    {tag}
                  </div>
                  {/* Discount badge */}
                  {discount && (
                    <div className="absolute top-2 right-2 w-10 h-10 flex items-center justify-center bg-rose-500 text-white text-xs font-black rounded-full shadow-md">
                      -{discount}%
                    </div>
                  )}
                  {/* Save button */}
                  <button
                    onClick={(e) => handleSave(product.id, e)}
                    className="absolute bottom-2 right-2 w-8 h-8 flex items-center justify-center bg-white/90 rounded-full shadow hover:bg-white transition-colors cursor-pointer"
                    aria-label="Save product"
                  >
                    <i className={`${saved[product.id] ? 'ri-heart-fill text-rose-500' : 'ri-heart-line text-gray-400'} text-sm`}></i>
                  </button>
                </div>

                {/* Info */}
                <div className="p-3 flex flex-col flex-1">
                  <p className="text-xs text-orange-500 font-semibold mb-1">{commLabel}</p>
                  <h3 className="text-sm font-bold text-gray-800 leading-snug line-clamp-2 mb-2 group-hover:text-orange-600 transition-colors">
                    {product.name}
                  </h3>

                  {/* Rating */}
                  <div className="flex items-center gap-1 mb-2">
                    <div className="flex items-center gap-0.5">
                      {[1, 2, 3, 4, 5].map((s) => (
                        <i
                          key={s}
                          className={`ri-star-${s <= Math.round(product.rating) ? 'fill' : 'line'} text-amber-400 text-xs`}
                        ></i>
                      ))}
                    </div>
                    <span className="text-xs text-gray-400">({product.reviews.toLocaleString()})</span>
                  </div>

                  {/* Price */}
                  <div className="flex items-center gap-2 mt-auto">
                    <span className="text-lg font-black text-gray-900">${product.price.toFixed(2)}</span>
                    {product.originalPrice && (
                      <span className="text-xs text-gray-400 line-through">${product.originalPrice.toFixed(2)}</span>
                    )}
                  </div>

                  {/* CTA */}
                  <div className="mt-3 flex items-center justify-between gap-2">
                    <span className="flex-1 text-center py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold rounded-lg transition-colors whitespace-nowrap">
                      Shop on Chewy →
                    </span>
                  </div>
                </div>
              </a>
            );
          })}
        </div>

        {/* Footer note */}
        <div className="mt-6 flex items-center justify-between">
          <p className="text-xs text-gray-400 flex items-center gap-1.5">
            <i className="ri-information-line text-orange-400"></i>
            MeowCentral earns a small commission on purchases — at no extra cost to you. It helps keep the site free! 🐾
          </p>
          <Link
            to="/shop"
            className="md:hidden flex items-center gap-1 text-sm font-semibold text-orange-600 hover:underline cursor-pointer whitespace-nowrap"
          >
            View All <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  );
}
